"use client";

import { useState } from "react";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { useRecentSearches } from "@/hooks/use-recent-searches";
import { useSavedSearchesList, useSelectedProject } from "@/hooks/use-search";
import {
  countAppliedFilters,
  describeSearchFilters,
} from "@/lib/search/search-ui";
import { formatRelativeTime } from "@/lib/date/format-relative-time";

import { Icon } from "@/components/ui/icon";
import { Pagination } from "@/components/ui/pagination";
import Link from "next/link";

const PER_PAGE = 10;

function PastSearchRowSkeletons({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="relative flex w-full shimmer shimmer-bg text-foreground items-center gap-6 overflow-hidden rounded-lg border border-contrast/5 bg-contrast/1 p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="px-1">
              <div className="h-5 max-w-60 rounded bg-contrast/6" />
            </div>
            <div className="px-1">
              <div className="h-[18px] max-w-[min(100%,280px)] rounded bg-contrast/4" />
            </div>
          </div>
          <div className="flex size-4 shrink-0 items-center justify-center opacity-30">
            <div className="size-4 rounded-sm bg-contrast/6" />
          </div>
        </div>
      ))}
    </>
  );
}

export function SearchHistory() {
  const { data: selectedProject } = useSelectedProject();
  const { pastSearchesOpen, closePastSearches } = useRecentSearches();
  const [page, setPage] = useState(1);

  const projectIdForQuery =
    pastSearchesOpen && selectedProject ? selectedProject.id : null;

  const { data, isPending, isFetching, isPlaceholderData } =
    useSavedSearchesList(projectIdForQuery, { page, per_page: PER_PAGE });
  /** Initial load or paginating (keepPreviousData shows stale list until fetch completes). */
  const showSearchSkeletons = isPending || (isFetching && isPlaceholderData);
  const searches = data?.searches ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const rangeStart = (page - 1) * PER_PAGE + 1;
  const rangeEnd = Math.min(page * PER_PAGE, total);

  if (!selectedProject) return null;

  return (
    <Sheet
      open={pastSearchesOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closePastSearches();
      }}
      size="sm"
    >
      <SheetHeader>
        <div className="flex items-center gap-1.5">
          <Icon
            name="IconSearchIntelligence"
            size={18}
            className="text-heading"
          />
          <SheetTitle className="inline-flex items-center gap-1.5">
            Past Searches in This Project
            {total > 0 && (
              <span className="text-lg font-medium text-contrast/50">
                {total}
              </span>
            )}
          </SheetTitle>
        </div>
        <SheetClose />
      </SheetHeader>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-background px-4 py-5 dark:bg-surface-elevated sm:px-6 sm:py-6">
        <div className="flex flex-col gap-2.5" aria-busy={showSearchSkeletons}>
          {showSearchSkeletons ? (
            <PastSearchRowSkeletons count={PER_PAGE} />
          ) : (
            searches.map((search) => {
              const filterCount = countAppliedFilters(search.filters ?? {});
              const timeAgo = formatRelativeTime(
                search.last_run_at ?? search.created_at,
              );
              const description =
                search.description ??
                describeSearchFilters(search.filters ?? {});
              return (
                <Link
                  key={search.id}
                  href={`/talent-search/${search.id}`}
                  className="relative flex items-center gap-6 w-full overflow-hidden bg-contrast/1 border border-contrast/5 rounded-lg p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] cursor-pointer hover:bg-contrast/3 transition-colors text-left"
                >
                  <div className="min-w-0 flex-1 flex flex-col gap-1">
                    <div className="min-w-0 flex gap-1.5 items-center px-1">
                      <span className="min-w-0 flex-1 truncate text-sm font-normal text-contrast/85 leading-5">
                        {search.name}
                      </span>
                      <span className="size-1 rounded-full bg-contrast/20 shrink-0" />
                      <div className="shrink-0 flex gap-1 items-center">
                        <Icon
                          name="IconFilter2"
                          size={14}
                          className="text-heading opacity-40"
                        />
                        <span className="text-sm font-normal text-contrast/65 leading-5">
                          {filterCount} Filter{filterCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <span className="size-1 rounded-full bg-contrast/20 shrink-0" />
                      <span className="shrink-0 text-sm font-normal text-contrast/45 leading-5">
                        {timeAgo}
                      </span>
                    </div>
                    <p className="text-[13px] font-normal text-contrast/70 truncate px-1">
                      {description}
                    </p>
                  </div>
                  <div className="shrink-0 opacity-30">
                    <Icon
                      name="IconArrowRight"
                      size={16}
                      className="text-heading"
                    />
                  </div>
                </Link>
              );
            })
          )}
          {!showSearchSkeletons && searches.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-1">
              <div className="opacity-40">
                <Icon
                  name="IconSearchIntelligence"
                  size={32}
                  className="text-heading"
                />
              </div>
              <p className="text-sm font-medium text-heading mt-1">
                No searches yet
              </p>
              <p className="text-xs text-contrast/60">
                Start a talent search to see results here.
              </p>
            </div>
          )}
        </div>
      </div>

      {total > PER_PAGE && (
        <Pagination.Root
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          variant="sheet"
        >
          <div className="bg-contrast/5 dark:bg-background/10 border-t border-border flex items-center justify-between gap-3 px-3 h-12 shrink-0">
            <span className="text-sm font-medium text-contrast/70 whitespace-nowrap shrink-0">
              {rangeStart} - {rangeEnd} of {total} Searches
            </span>
            <Pagination.Row>
              <Pagination.Prev />
              <Pagination.Pages
                strategy="window"
                maxVisible={4}
                windowAlign="center"
              />
              <Pagination.Next />
            </Pagination.Row>
          </div>
        </Pagination.Root>
      )}
    </Sheet>
  );
}
